import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';

export interface ExternalLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> {
  href: string;
  children: ReactNode;
  /** Hide the screen-reader announcement (e.g. when an inline icon already conveys it). */
  suppressNewTabAnnouncement?: boolean;
}

/**
 * Renders an off-site anchor that always opens in a new tab with safe rel
 * attributes and a screen-reader hint that the link opens in a new tab.
 * Use for any href starting with "http(s)://" pointing off-site.
 */
export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  ({ href, children, suppressNewTabAnnouncement, ...rest }, ref) => (
    <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
      {!suppressNewTabAnnouncement && <span className="sr-only"> (opens in new tab)</span>}
    </a>
  ),
);
ExternalLink.displayName = 'ExternalLink';

export default ExternalLink;
